import { LowerBool } from "./Screens";
import guidGenerator, {
  getCurrentDate,
} from "../features/utils/commonFunctions";
import { EventKind, Kind } from "../features/fsm/types";
import * as Sentry from "@sentry/react";

export type AssetURL =
  | { __tag: "string"; value: string }
  | { __tag: "file"; value: File };

export function throwIfWrong(expected: string, asset: AssetURL) {
  if (asset.__tag !== expected) {
    throw new Error(
      `Oops. This asset is of type ${asset.__tag} but MUST be ${expected} in this context.`
    );
  }
}

export class Asset {
  created: string;
  externalURL: AssetURL;
  id: string;
  isBakedAsset: LowerBool;
  shouldNotAttemptUpload: LowerBool;
  treemoContentID: string;
  updated: string;

  constructor(
    created = getCurrentDate(),
    externalURL: AssetURL,
    id: string,
    isBakedAsset: LowerBool,
    shouldNotAttemptUpload: LowerBool,
    treemoContentID: string,
    updated = getCurrentDate()
  ) {
    this.created = created;
    this.externalURL = externalURL;
    this.id = id;
    this.isBakedAsset = isBakedAsset;
    this.shouldNotAttemptUpload = shouldNotAttemptUpload;
    this.treemoContentID = treemoContentID;
    this.updated = updated;
  }

  static fromJSON(json: any): Asset {
    let asset = Object.create(Asset.prototype);
    return Object.assign(asset, json, {});
  }

  static reviver(key: string, value: any): any {
    return key === "" ? Asset.fromJSON(value) : value;
  }

  static default(src: AssetURL): Asset {
    return new Asset(
      getCurrentDate(),
      src,
      guidGenerator(),
      "no",
      "no",
      "",
      getCurrentDate()
    );
  }
}

export default class ImageAsset extends Asset implements Kind {
  __hasModifications: boolean;
  __name: string;

  constructor(
    created = getCurrentDate(),
    externalURL: AssetURL,
    id: string,
    isBakedAsset: LowerBool,
    shouldNotAttemptUpload: LowerBool,
    treemoContentID: string,
    updated = getCurrentDate(),
    __hasModification = false,
    __name = ""
  ) {
    super(
      created,
      externalURL,
      id,
      isBakedAsset,
      shouldNotAttemptUpload,
      treemoContentID,
      updated
    );
    this.__hasModifications = __hasModification;
    this.__name = __name;
  }
  eventKind(): EventKind {
    return EventKind.Flow;
  }

  static fromJSON(json: any): ImageAsset {
    let asset = Object.create(ImageAsset.prototype);
    if (!json) return undefined!;

    // Get the URL and handle various invalid cases
    let urlValue = json.externalURL;

    // If externalURL is already an object with __tag, extract the value
    if (urlValue && typeof urlValue === "object" && urlValue.__tag === "string") {
      urlValue = urlValue.value;
    }

    return Object.assign(asset, json, {
      externalURL: { __tag: "string", value: enforceHTTPS(urlValue) },
    });
  }

  static reviver(key: string, value: any): any {
    return key === "" ? ImageAsset.fromJSON(value) : value;
  }

  static default(
    src: AssetURL,
    hasModification: boolean = false,
    name: string = ""
  ): ImageAsset {
    return new ImageAsset(
      getCurrentDate(),
      src,
      guidGenerator(),
      "no",
      "no",
      "",
      getCurrentDate(),
      hasModification,
      name
    );
  }
}

ImageAsset.displayName = "ImageAsset";

function enforceHTTPS(url: string | undefined): string | undefined {
  if (!url) return undefined;
  if (typeof url != "string") return url;

  // Return early for relative paths and already-secure URLs
  if (url.startsWith("/include/img/creator") || url.startsWith("https://"))
    return url;

  // Return early for empty strings or whitespace-only strings
  if (url.trim() === "") return undefined;

  try {
    // Only attempt URL parsing if it looks like a valid URL
    // Check if it has a protocol or starts with //
    if (!url.includes("://") && !url.startsWith("//")) {
      // Assume it's a relative path or invalid, return as-is
      console.warn("Invalid URL format, returning as-is:", url);
      return url;
    }

    let newUrl = new URL(url);
    newUrl.hostname = "media.goboard.io";
    newUrl.protocol = "https:";
    return newUrl.toString();
  } catch (error) {
    // Log the specific URL that failed
    console.error("Failed to parse URL:", url, error);
    Sentry.captureException(error);
    return url;
  }
}

// @ts-ignore
export class PdfAsset extends Asset {
  sourceFileName: string;

  constructor(
    created = getCurrentDate(),
    externalURL: AssetURL,
    id: string,
    isBakedAsset: LowerBool,
    shouldNotAttemptUpload: LowerBool,
    treemoContentID: string,
    updated = getCurrentDate(),
    sourceFileName: string
  ) {
    super(
      created,
      externalURL,
      id,
      isBakedAsset,
      shouldNotAttemptUpload,
      treemoContentID,
      updated
    );
    this.sourceFileName = sourceFileName;
  }

  static fromJSON(json: any): PdfAsset {
    let asset = Object.create(PdfAsset.prototype);
    return Object.assign(asset, json, {});
  }

  static reviver(key: string, value: any): any {
    return key === "" ? PdfAsset.fromJSON(value) : value;
  }

  static default(src: AssetURL, name: string): PdfAsset {
    return new PdfAsset(
      getCurrentDate(),
      src,
      guidGenerator(),
      "no",
      "no",
      "",
      getCurrentDate(),
      name
    );
  }
}

PdfAsset.displayName = "PdfAsset";
